/**
 * CreateAdminMasterModal - Modal para criação de novos Admin Masters
 * Com sistema de verificação de senha mestre e níveis de permissão
 * Watch Smart Media Technologies
 */

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Shield, Lock, Mail, Key, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';

interface CreateAdminMasterModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    currentUserEmail: string;
}

type ModalStep = 'password' | 'form';

const PERMISSION_LEVELS = [
    { level: 1, label: 'Nível 1 - Visualização Básica', description: 'Acesso apenas para visualizar dados' },
    { level: 2, label: 'Nível 2 - Gestão de Clientes', description: 'Pode gerenciar clientes e mídias' },
    { level: 3, label: 'Nível 3 - Gestão de Planos', description: 'Pode criar e editar planos' },
    { level: 4, label: 'Nível 4 - Gestão Financeira', description: 'Acesso a dados financeiros' },
    { level: 5, label: 'Nível 5 - Admin Total', description: 'Acesso completo ao sistema' },
];

export const CreateAdminMasterModal: React.FC<CreateAdminMasterModalProps> = ({
    open,
    onOpenChange,
    currentUserEmail
}) => {
    const { toast } = useToast();

    // Estado do fluxo
    const [step, setStep] = useState<ModalStep>('password');
    const [isLoading, setIsLoading] = useState(false);

    // Estado da verificação de senha
    const [masterPassword, setMasterPassword] = useState('');
    const [showMasterPassword, setShowMasterPassword] = useState(false);
    const [passwordError, setPasswordError] = useState('');

    // Estado do formulário de criação
    const [newAdminEmail, setNewAdminEmail] = useState('');
    const [newAdminPassword, setNewAdminPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [selectedLevels, setSelectedLevels] = useState<number[]>([]);

    // Resetar estado ao fechar
    const handleClose = () => {
        setStep('password');
        setMasterPassword('');
        setPasswordError('');
        setNewAdminEmail('');
        setNewAdminPassword('');
        setConfirmPassword('');
        setSelectedLevels([]);
        setShowMasterPassword(false);
        setShowNewPassword(false);
        onOpenChange(false);
    };

    // Verificar senha mestre
    const handleVerifyPassword = async () => {
        if (!masterPassword.trim()) {
            setPasswordError('Digite a senha mestre');
            return;
        }

        // Verificar se é o usuário correto
        if (currentUserEmail !== 'master@admin.com') {
            setPasswordError('Acesso negado. Apenas master@admin.com pode criar outros admins.');
            return;
        }

        setIsLoading(true);
        setPasswordError('');

        try {
            // Verificar senha via RPC
            const { data, error } = await supabase.rpc('verify_master_password', {
                p_email: currentUserEmail,
                p_password: masterPassword
            });

            if (error) {
                console.error('Erro ao verificar senha:', error);
                // Fallback: verificar localmente se a função RPC não existir
                if (masterPassword === 'Adminprogrammer21*') {
                    setStep('form');
                    toast({
                        title: "Acesso liberado",
                        description: "Você pode criar um novo Admin Master.",
                    });
                    return;
                }
                setPasswordError('Erro ao verificar senha. Tente novamente.');
                return;
            }

            if (data === true) {
                setStep('form');
                toast({
                    title: "Acesso liberado",
                    description: "Você pode criar um novo Admin Master.",
                });
            } else {
                setPasswordError('Senha incorreta. Tente novamente.');
            }
        } catch (error) {
            console.error('Erro:', error);
            // Fallback local
            if (masterPassword === 'Adminprogrammer21*' && currentUserEmail === 'master@admin.com') {
                setStep('form');
                return;
            }
            setPasswordError('Erro ao verificar senha.');
        } finally {
            setIsLoading(false);
        }
    };

    // Alternar seleção de nível
    const toggleLevel = (level: number) => {
        setSelectedLevels(prev =>
            prev.includes(level)
                ? prev.filter(l => l !== level)
                : [...prev, level].sort((a, b) => a - b)
        );
    };

    // Criar novo admin master
    const handleCreateAdmin = async () => {
        // Validações
        if (!newAdminEmail.trim()) {
            toast({ title: "Erro", description: "Digite o email do novo admin.", variant: "destructive" });
            return;
        }

        if (!newAdminEmail.toLowerCase().includes('admin')) {
            toast({ title: "Erro", description: "O email do admin deve conter 'admin'.", variant: "destructive" });
            return;
        }

        if (!newAdminPassword || newAdminPassword.length < 8) {
            toast({ title: "Erro", description: "A senha deve ter no mínimo 8 caracteres.", variant: "destructive" });
            return;
        }

        if (newAdminPassword !== confirmPassword) {
            toast({ title: "Erro", description: "As senhas não conferem.", variant: "destructive" });
            return;
        }

        if (selectedLevels.length === 0) {
            toast({ title: "Erro", description: "Selecione pelo menos um nível de permissão.", variant: "destructive" });
            return;
        }

        setIsLoading(true);

        try {
            // Criar usuário no Supabase Auth
            const { data: authData, error: authError } = await supabase.auth.admin.createUser({
                email: newAdminEmail,
                password: newAdminPassword,
                email_confirm: true
            });

            // Se admin.createUser falhar (não é admin do Supabase), usar signUp normal
            if (authError) {
                console.log('Tentando criar via signUp...');
                const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
                    email: newAdminEmail,
                    password: newAdminPassword,
                    options: {
                        data: {
                            is_admin_master: true,
                            admin_permission_levels: selectedLevels
                        }
                    }
                });

                if (signUpError) {
                    console.error('Erro ao criar usuário:', signUpError);
                    toast({
                        title: "Erro",
                        description: signUpError.message || "Não foi possível criar o admin.",
                        variant: "destructive"
                    });
                    return;
                }

                if (signUpData.user) {
                    // Atualizar perfil com níveis de permissão e flag de admin
                    const { error: profileError } = await supabase
                        .from('profiles')
                        .update({
                            is_admin_master: true,
                            admin_permission_levels: selectedLevels
                        })
                        .eq('id', signUpData.user.id);

                    if (profileError) {
                        console.error('Erro ao atualizar perfil:', profileError);
                    }

                    toast({
                        title: "Admin Master criado!",
                        description: `O admin ${newAdminEmail} foi criado com sucesso. Níveis: ${selectedLevels.join(', ')}`,
                    });

                    handleClose();
                    return;
                }
            }

            // Se admin.createUser funcionou
            if (authData?.user) {
                // Atualizar perfil com níveis de permissão
                const { error: profileError } = await supabase
                    .from('profiles')
                    .update({
                        is_admin_master: true,
                        admin_permission_levels: selectedLevels
                    })
                    .eq('id', authData.user.id);

                if (profileError) {
                    console.error('Erro ao atualizar perfil:', profileError);
                }

                toast({
                    title: "Admin Master criado!",
                    description: `O admin ${newAdminEmail} foi criado com sucesso. Níveis: ${selectedLevels.join(', ')}`,
                });

                handleClose();
            }
        } catch (error) {
            console.error('Erro ao criar admin:', error);
            toast({
                title: "Erro",
                description: "Ocorreu um erro ao criar o admin. Tente novamente.",
                variant: "destructive"
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-purple-600" />
                        {step === 'password' ? 'Verificação de Segurança' : 'Criar Novo Admin Master'}
                    </DialogTitle>
                    <DialogDescription>
                        {step === 'password'
                            ? 'Digite a senha mestre para acessar esta funcionalidade.'
                            : 'Preencha os dados do novo administrador master.'
                        }
                    </DialogDescription>
                </DialogHeader>

                {step === 'password' ? (
                    // Etapa 1: Verificação de senha
                    <div className="space-y-4 py-4">
                        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                            <p className="text-sm text-amber-800">
                                Esta é uma área restrita.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="masterPassword">Senha Mestre</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                    id="masterPassword"
                                    type={showMasterPassword ? 'text' : 'password'}
                                    value={masterPassword}
                                    onChange={(e) => setMasterPassword(e.target.value)}
                                    placeholder="Digite a senha mestre"
                                    className="pl-10 pr-10"
                                    onKeyDown={(e) => e.key === 'Enter' && handleVerifyPassword()}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowMasterPassword(!showMasterPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showMasterPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {passwordError && (
                                <p className="text-sm text-red-500 flex items-center gap-1">
                                    <AlertCircle className="h-4 w-4" />
                                    {passwordError}
                                </p>
                            )}
                        </div>
                    </div>
                ) : (
                    // Etapa 2: Formulário de criação
                    <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                        {/* Email */}
                        <div className="space-y-2">
                            <Label htmlFor="newAdminEmail">Email do Novo Admin</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                    id="newAdminEmail"
                                    type="email"
                                    value={newAdminEmail}
                                    onChange={(e) => setNewAdminEmail(e.target.value)}
                                    placeholder="novoadmin@admin.com"
                                    className="pl-10"
                                />
                            </div>
                            <p className="text-xs text-gray-500">O email deve conter "admin" para ser reconhecido como administrador.</p>
                        </div>

                        {/* Senha */}
                        <div className="space-y-2">
                            <Label htmlFor="newAdminPassword">Senha</Label>
                            <div className="relative">
                                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                    id="newAdminPassword"
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={newAdminPassword}
                                    onChange={(e) => setNewAdminPassword(e.target.value)}
                                    placeholder="Mínimo 8 caracteres"
                                    className="pl-10 pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        {/* Confirmar Senha */}
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirmar Senha</Label>
                            <div className="relative">
                                <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                <Input
                                    id="confirmPassword"
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Repita a senha"
                                    className="pl-10"
                                />
                            </div>
                            {confirmPassword && newAdminPassword !== confirmPassword && (
                                <p className="text-sm text-red-500">As senhas não conferem</p>
                            )}
                            {confirmPassword && newAdminPassword === confirmPassword && (
                                <p className="text-sm text-green-500 flex items-center gap-1">
                                    <CheckCircle className="h-4 w-4" />
                                    Senhas conferem
                                </p>
                            )}
                        </div>

                        {/* Níveis de Permissão */}
                        <div className="space-y-3">
                            <Label>Níveis de Permissão</Label>
                            <div className="border rounded-lg p-3 space-y-3 bg-gray-50">
                                {PERMISSION_LEVELS.map((item) => (
                                    <div
                                        key={item.level}
                                        className={`flex items-start gap-3 p-2 rounded-md transition-colors cursor-pointer ${selectedLevels.includes(item.level) ? 'bg-purple-100' : 'hover:bg-gray-100'
                                            }`}
                                        onClick={() => toggleLevel(item.level)}
                                    >
                                        <Checkbox
                                            id={`level-${item.level}`}
                                            checked={selectedLevels.includes(item.level)}
                                            onCheckedChange={() => toggleLevel(item.level)}
                                            className="mt-1"
                                        />
                                        <div className="flex-1">
                                            <label
                                                htmlFor={`level-${item.level}`}
                                                className="text-sm font-medium cursor-pointer"
                                            >
                                                {item.label}
                                            </label>
                                            <p className="text-xs text-gray-500">{item.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {selectedLevels.length > 0 && (
                                <p className="text-sm text-purple-600">
                                    Níveis selecionados: {selectedLevels.join(', ')}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={handleClose} disabled={isLoading}>
                        Cancelar
                    </Button>
                    {step === 'password' ? (
                        <Button
                            onClick={handleVerifyPassword}
                            disabled={isLoading || !masterPassword.trim()}
                            className="bg-purple-600 hover:bg-purple-700"
                        >
                            {isLoading ? 'Verificando...' : 'Verificar'}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleCreateAdmin}
                            disabled={isLoading || !newAdminEmail || !newAdminPassword || selectedLevels.length === 0}
                            className="bg-green-600 hover:bg-green-700"
                        >
                            {isLoading ? 'Criando...' : 'Criar Admin Master'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default CreateAdminMasterModal;
